/**
 * Everything the server refuses bounces as a type-error message: the R4
 * oversize cap, posting to an archived group or one the sender is not in, and
 * a refused retraction.
 */
import type { Element as XmlEl } from "@xmpp/xml"
import {
  NS_GROUP_ERRORS,
  NS_RETRACTION_ERRORS,
  NS_STANZAS,
} from "../../constants"
import { toJid, type Jid } from "../../jid"

import { attr } from "../../xmlUtils"

export type MessageBounce = {
  peer: Jid
  /** The rejected message's id — for a retraction, the message it targeted. */
  messageId?: string
  /** RFC 6120, e.g. `not-acceptable`, `policy-violation`. */
  condition?: string
  text?: string
  /** Takes precedence over `condition`: the two overlap on `not-acceptable`. */
  refusal: Refusal | null
}

export const isErrorMessage = (stanza: XmlEl): boolean =>
  attr(stanza, "type") === "error"

export const readMessageBounce = (stanza: XmlEl): MessageBounce | null => {
  const from = attr(stanza, "from")
  if (!from) return null

  const error = stanza.getChild("error")

  return {
    peer: toJid(from),
    messageId: attr(stanza, "id"),
    condition: readCondition(error),
    text: error?.getChildText("text", NS_STANZAS) ?? undefined,
    refusal: readRefusal(error),
  }
}

/** By elimination, so a condition we've never heard of is still named. */
const readCondition = (error: XmlEl | undefined): string | undefined =>
  error
    ?.getChildElements()
    .find((child) => child.getNS() === NS_STANZAS && child.getName() !== "text")
    ?.getName()

/** As Costanza spells them on the wire. */
export const RETRACTION_REFUSALS = [
  "expired",
  "already-retracted",
  "not-found",
  "forbidden",
  "malformed",
  "forged-tombstone",
] as const

export const GROUP_REFUSALS = ["archived", "not-member"] as const

export type RetractionRefusal = (typeof RETRACTION_REFUSALS)[number]
export type GroupRefusal = (typeof GROUP_REFUSALS)[number]

/** Every reason a bounce can name, whichever action was refused. */
export type Refusal = RetractionRefusal | GroupRefusal

export const isRetractionRefusal = (
  refusal: Refusal
): refusal is RetractionRefusal =>
  (RETRACTION_REFUSALS as readonly Refusal[]).includes(refusal)

/**
 * Takes the `<error>` element. Most refusals of either kind bounce as
 * `not-acceptable` — this marker is the only thing telling them apart.
 */
export const readRefusal = (error: XmlEl | undefined): Refusal | null =>
  RETRACTION_REFUSALS.find((reason) =>
    error?.getChild(reason, NS_RETRACTION_ERRORS)
  ) ??
  GROUP_REFUSALS.find((reason) => error?.getChild(reason, NS_GROUP_ERRORS)) ??
  null
